import { createContext, useReducer, useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../../api';
import { useEventListener } from 'use-event-listeners';
import { useDispatch } from 'react-redux';
import { logoutUser, setToken, setUser } from '../../store/auth';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { refreshSession } from '../../utils/api/user';
import { batch } from 'react-redux';
import { decodeJwt } from '../../utils/file';
import { useRootMemoSelector } from 'use-redux-states';

const initialState = {
  signOutInterval: null,
  inactiveTimer: 0,
  interval: null,
  userdetails: null,
  refreshToken: null // <-- add this
};

const AuthContext = createContext<{
  authDispatch: React.ActionDispatch<[action: any]>;
  authState: typeof initialState;
}>({ authState: initialState, authDispatch: () => null });
const AuthConsumer = AuthContext.Consumer;

const reducer = () => (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        userdetails: action.payload.user,
        refreshToken: action.payload.refreshToken // <-- store refreshToken
      };
    case 'LOGOUT':
      clearInterval(state.interval);
      return { ...state, refreshToken: null };
    case 'SESSION_TIMEOUT':
      clearInterval(state.interval);
      return { ...state, signOutInterval: action.payload, inactiveTimer: action.payload };
    default:
      return state;
  }
};

const source_url = window.location.href;

const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [authState, authDispatch] = useReducer(reducer(), initialState);
  const [{ ip }, setState] = useState({
    ip: undefined
  });
  const confirmRef = useRef<{ handleOpen?: () => void; handleClose?: () => void } | null>(null);
  const token = useRootMemoSelector('auth.token');
  const refreshToken = authState.refreshToken;

  useEventListener({
    listeners: { 'log-out': () => authDispatch({ type: 'LOGOUT' }) }
  });

  useEffect(() => {
    axios
      .get('https://ipapi.co/json/')
      .then((data) => {
        const { ip } = data.data;

        setState({
          ip
        });
      })
      .catch((error) => console.log('error => ', error));
  }, []);

  const handleInactivity = () => {
    if (!authState.signOutInterval) return;

    return (authState.interval = setInterval(() => {
      authState.inactiveTimer -= 1000;

      if (authState.inactiveTimer === 60000) {
        handlelogoutConfirmation();
      } else if (authState.inactiveTimer <= 0) {
        toast.info('Session expired', { autoClose: 2000 });
        logoutAction();
      }
    }, 1000));
  };

  const handlelogoutConfirmation = () => confirmRef.current?.handleOpen?.();

  const handleRefreshToken = async () => {
    try {
      if (!refreshToken) {
        return false;
      }

      const { data } = await refreshSession(refreshToken);

      if (data?.token && data?.refreshToken) {
        batch(() => {
          dispatch(setToken(data.token));
          dispatch(setUser(data.user));
        });
        authDispatch({
          type: 'LOGIN',
          payload: {
            user: data.user,
            refreshToken: data.refreshToken
          }
        });
        return true;
      } else {
        console.error('Invalid refresh response');
        return false;
      }
    } catch (error: any) {
      console.error('Error refreshing token:', error);
      // If refresh fails, logout the user
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        toast.info('Session expired, please login again', { autoClose: 2000 });
        logoutAction();
      }
      return false;
    }
  };

  const handleRejectLogoutAction = async () => {
    try {
      clearInterval(authState.interval);
      handleInactivity();
      await handleRefreshToken();
      return;
    } catch (error) {
      toast.info('error refreshing the page', { autoClose: 2000 });
      return;
    }
  };

  events.forEach((item) => {
    window.addEventListener(item, () => {
      authState.inactiveTimer = authState.signOutInterval;
    });
  });

  const logoutAction = async () => {
    try {
      events.forEach((item) => {
        window.removeEventListener(item, () => {
          authState.inactiveTimer = authState.signOutInterval;
        });
      });
      clearInterval(authState.interval);

      window.location.href = '/auth/login';

      confirmRef.current?.handleClose?.();

      if (authState.userdetails) {
        try {
          await api.post('/logout', { source_url, ip, user: authState.userdetails });
        } catch (error) {
          console.log(error);
        }
      }

      dispatch(logoutUser());
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'An error occurred', {
        autoClose: 2000
      });
    }
  };

  useEffect(() => {
    if (!authState.signOutInterval) {
      return;
    } else if (window.location.pathname !== '/auth/login') {
      const interval = handleInactivity();

      return () => clearInterval(interval);
    } else {
      clearInterval(authState.interval);
    }
  }, [authState.signOutInterval]);

  useEffect(() => {
    const checkTokenAndRefresh = async () => {
      if (token && refreshToken) {
        const decodedToken = decodeJwt(token);

        if (decodedToken?.exp) {
          const now = Date.now();
          const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
          const oneMinuteBeforeExpiration = expirationTime - 1 * 60 * 1000;

          if (expirationTime < now) {
            toast.info('Session expired', { autoClose: 2000 });
            logoutAction();
          } else if (
            now >= oneMinuteBeforeExpiration &&
            window.location.pathname !== '/auth/login'
          ) {
            // Refresh token when we're within 1 minute of expiration

            await handleRefreshToken();
          }
        }
      }
    };

    // Check immediately and then every minute
    checkTokenAndRefresh();
    const interval = setInterval(checkTokenAndRefresh, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [token, refreshToken, window.location.pathname]);

  useEffect(() => {
    // On pagehide, only log out if not persisted (not a refresh/back-forward)
    const handlePageHide = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        logoutAction();
      }
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ authState, authDispatch }}>
      <ConfirmDialog
        ref={confirmRef}
        handleYes={handleRejectLogoutAction}
        handleLogout={logoutAction}
        content={
          <div className='text-center'>
            <div>You are about to be signed out due to inactivity</div>
            <div>
              Do you want to <strong>stay signed in</strong>?
            </div>
          </div>
        }
      />
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider, AuthConsumer };
